import { observer } from "mobx-react";
import {
  Box,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Link,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import {
  TAccountIntegrationData,
  TReapitConfig,
} from "types/account-integration.type";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { ApiMessageStack } from "components";
import React, { ChangeEvent, FC, useCallback } from "react";

interface IProps {
  accountIntegrationsStore: AccountIntegrationsStore;
  accountIntegration: TAccountIntegrationData<TReapitConfig>;
}

const LEAD_TYPE_TOGGLES = [
  { key: "autoSyncSaleLeads" as const, label: "Sale leads", color: "#00BF63" },
  { key: "autoSyncLetLeads" as const, label: "Let leads", color: "#00BF63" },
  {
    key: "autoSyncVendorLeads" as const,
    label: "Vendor leads",
    color: "#960DF2",
  },
  {
    key: "autoSyncLandlordLeads" as const,
    label: "Landlord leads",
    color: "#960DF2",
  },
];

export const ReapitAutoSyncConfigurator: FC<IProps> = observer(
  ({ accountIntegrationsStore, accountIntegration }) => {
    const toast = useToast();
    const isAutoSyncEnabled = !accountIntegration.isManuallyTriggered;
    const integrationConfig = accountIntegration.integrationConfig;

    const updateIntegration = useCallback(
      async (updates: {
        isManuallyTriggered?: boolean;
        integrationConfig?: Partial<TReapitConfig>;
      }) => {
        const { id, type, active } = accountIntegration;
        try {
          await accountIntegrationsStore.updateAccountIntegration(id, {
            type,
            active,
            isManuallyTriggered:
              updates.isManuallyTriggered ??
              accountIntegration.isManuallyTriggered,
            integrationConfig: {
              enableApplicantRequirements:
                integrationConfig.enableApplicantRequirements,
              areApplicantRequirementsFieldsMandatory:
                integrationConfig.areApplicantRequirementsFieldsMandatory,
              ...updates.integrationConfig,
            },
          });
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={
                  "Reapit integration configuration successfully updated."
                }
              />
            ),
          });
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [accountIntegration, accountIntegrationsStore, integrationConfig, toast]
    );

    const handleAutoSyncToggle = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        const isEnabled = event.currentTarget.checked;
        await updateIntegration({
          isManuallyTriggered: !isEnabled,
        });
      },
      [updateIntegration]
    );

    const handleScrollToDefaultNegotiator = useCallback(() => {
      document
        .getElementById("default-negotiator-mapping")
        ?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const handleLeadTypeToggle = useCallback(
      async (key: typeof LEAD_TYPE_TOGGLES[number]["key"], value: boolean) => {
        await updateIntegration({
          integrationConfig: { [key]: value },
        });
      },
      [updateIntegration]
    );

    return (
      <Box mb={6}>
        <Box fontSize={"xl"} color={"gray.700"} mb={4}>
          Automatic Lead sync
        </Box>
        <Grid templateColumns="75% 25%" rowGap={4}>
          <VStack alignItems={"left"}>
            <Box color={"gray.700"}>Enable automatic lead sync</Box>
            <Box fontSize={"sm"} maxWidth={"580px"} color={"leadpro.400"}>
              With automatic lead sync enabled, your selected lead types below
              will automatically sync into reapit with their associated office,
              and default negotiator supplied below.
            </Box>
            {isAutoSyncEnabled && (
              <>
                <HStack spacing={4} pt={2}>
                  {LEAD_TYPE_TOGGLES.map(({ key, label, color }) => (
                    <HStack key={key} spacing={2}>
                      <Box
                        w={"6px"}
                        h={"14px"}
                        borderRadius={"full"}
                        bg={color}
                        flexShrink={0}
                      />
                      <Text
                        fontSize={"sm"}
                        fontWeight={"medium"}
                        color={"leadpro.400"}
                      >
                        {label}
                      </Text>
                      <Switch
                        size={"sm"}
                        colorScheme={"blue"}
                        isChecked={integrationConfig[key] ?? true}
                        onChange={event =>
                          handleLeadTypeToggle(key, event.currentTarget.checked)
                        }
                      />
                    </HStack>
                  ))}
                </HStack>
                <Box fontSize={"sm"} color={"leadpro.400"} pt={2}>
                  Automatically synced leads require a default negotiator to be
                  mapped, which will be associated with all synced leads.{" "}
                  <Link
                    color={"blue.500"}
                    cursor={"pointer"}
                    onClick={handleScrollToDefaultNegotiator}
                  >
                    Go to default negotiator mappings
                  </Link>
                  .
                </Box>
              </>
            )}
          </VStack>
          <Flex justifyContent={"center"} alignItems={"flex-start"} pt={1}>
            <Checkbox
              size={"lg"}
              isChecked={isAutoSyncEnabled}
              onChange={handleAutoSyncToggle}
            />
          </Flex>
        </Grid>
      </Box>
    );
  }
);
